import { useEffect, useMemo, useState } from "react";
import { GitPullRequestArrow, Loader2, Palette, Save, Scale, ShieldCheck, ShieldX, SlidersHorizontal, Smartphone, Timer, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    type MFAFactor,
    useDeleteMFAFactor,
    useMFAFactors,
} from "~/lib/mfa";
import {
    useApprovalGates,
    useFeedSourceWeights,
    usePatchApprovalGates,
    usePatchFeedSourceWeights,
    usePatchScoreConfig,
    usePatchSettings,
    useScoreConfig,
    useSettings,
} from "~/lib/settings";

import { EnrollTOTPDialog } from "~/components/EnrollTOTPDialog";

const KIND_LABEL: Record<MFAFactor["kind"], string> = {
    totp: "Authenticator app (TOTP)",
    webauthn: "Passkey / security key",
    recovery_code: "Recovery code",
};

export function SettingsPage(): React.ReactElement {
    const me = useCurrentUser();
    const factors = useMFAFactors();
    const remove = useDeleteMFAFactor();
    const hasMFA = (factors.data?.length ?? 0) > 0;
    const isAdmin = (me.data?.roles ?? []).includes("admin");

    async function doRemove(f: MFAFactor): Promise<void> {
        if (!confirm(`Remove "${f.label}"? You'll need to enroll another factor to keep MFA on.`)) return;
        try {
            await remove.mutateAsync(f.id);
            toast.success("Factor removed");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to remove factor");
        }
    }

    return (
        <div className="stack-lg" style={{ maxWidth: 920, margin: "0 auto" }}>
            <header className="stack-xs">
                <div className="cluster-xs text-text-muted">
                    <UserCog size={16} className="text-accent" />
                    <span className="type-caption">Account</span>
                </div>
                <h1 className="type-display-sm">Your account</h1>
                {me.data && (
                    <div className="cluster-xs text-13 text-text-muted">
                        <span>{me.data.email}</span>
                        <span>·</span>
                        <span className="cluster-xs">
                            {me.data.roles.map((r) => <span key={r} className="chip chip--accent">{r}</span>)}
                        </span>
                    </div>
                )}
            </header>

            <section className="card stack">
                <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                    <div className="stack-xs">
                        <h2 className="type-h2">Two-factor authentication</h2>
                        <p className="text-13 text-text-muted" style={{ maxWidth: 520 }}>
                            Add a TOTP authenticator app to require a 6-digit code on every sign-in. WebAuthn /
                            passkeys land next; for now TOTP gives the same protection against password leaks.
                        </p>
                    </div>
                    {!hasMFA && (
                        <span className="chip chip--warn" title="No second factor enrolled">
                            recommended
                        </span>
                    )}
                    {hasMFA && (
                        <span className="chip chip--success" title="At least one factor enrolled">
                            enabled
                        </span>
                    )}
                </div>

                {factors.isLoading && (
                    <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                        <Loader2 size={18} className="empty-state__icon animate-spin" aria-hidden />
                        <div className="empty-state__body">Loading factors…</div>
                    </div>
                )}
                {factors.isError && (
                    <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                        <ShieldX size={18} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__body">Could not load factors.</div>
                    </div>
                )}
                {factors.data && (
                    factors.data.length === 0 ? (
                        <div className="empty-state" style={{ padding: "var(--space-6)" }}>
                            <ShieldCheck size={20} className="empty-state__icon" aria-hidden />
                            <div className="empty-state__title">No second factor yet</div>
                            <div className="empty-state__body">
                                Sign-ins currently rely on your password alone. Add an authenticator app
                                below to require a one-time code on every sign-in.
                            </div>
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table className="data-table data-table--dense">
                                <thead>
                                    <tr>
                                        <th>Factor</th>
                                        <th>Label</th>
                                        <th>Last used</th>
                                        <th>Added</th>
                                        <th style={{ width: 1 }} aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {factors.data.map((f) => (
                                        <tr key={f.id}>
                                            <td>
                                                <span className="cluster-xs">
                                                    <Smartphone size={14} className="text-accent" />
                                                    <span className="text-13">{KIND_LABEL[f.kind]}</span>
                                                </span>
                                            </td>
                                            <td className="text-13">{f.label}</td>
                                            <td className="text-12 text-text-muted">
                                                {f.lastUsedAt
                                                    ? new Date(f.lastUsedAt).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                            <td className="text-12 text-text-muted">
                                                {new Date(f.createdAt).toLocaleDateString()}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                <button
                                                    type="button"
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() => void doRemove(f)}
                                                    disabled={remove.isPending}
                                                    title="Remove"
                                                    aria-label="Remove factor"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                <div style={{ marginTop: "var(--space-2)" }}>
                    <EnrollTOTPDialog />
                </div>
            </section>

            {isAdmin && <PlatformSettingsSections />}
        </div>
    );
}

// ---- Phase 1G admin-only sections ----------------------------------------

function PlatformSettingsSections(): React.ReactElement {
    return (
        <>
            <RetentionSection />
            <ScoreConfigSection />
            <ApprovalGatesSection />
            <BrandingSection />
            <SourceWeightsSection />
        </>
    );
}

const RETENTION_KEYS = [
    { key: "retention.iocs_days", label: "IoCs", min: 7, max: 3650 },
    { key: "retention.cves_days", label: "CVEs", min: 30, max: 3650 },
    { key: "retention.audit_days", label: "Audit log", min: 30, max: 3650 },
    { key: "retention.workflow_runs_days", label: "Workflow runs", min: 7, max: 3650 },
    // Phase 1G leftover #1 — three additional retention windows.
    { key: "retention.notifications_days", label: "Notifications", min: 7, max: 3650 },
    { key: "retention.feed_samples_days", label: "Feed samples", min: 7, max: 3650 },
    { key: "retention.lookup_logs_days", label: "Portal lookup logs", min: 7, max: 3650 },
] as const;

function RetentionSection(): React.ReactElement {
    const settings = useSettings();
    const patch = usePatchSettings();
    const [draft, setDraft] = useState<Record<string, number>>({});

    useEffect(() => {
        if (!settings.data) return;
        const next: Record<string, number> = {};
        for (const { key } of RETENTION_KEYS) {
            const v = settings.data[key];
            if (typeof v === "number") next[key] = v;
        }
        setDraft(next);
    }, [settings.data]);

    const dirty = useMemo(() => {
        if (!settings.data) return false;
        for (const { key } of RETENTION_KEYS) {
            if (draft[key] !== settings.data[key]) return true;
        }
        return false;
    }, [draft, settings.data]);

    async function save(): Promise<void> {
        try {
            const updates: Record<string, number> = {};
            for (const { key } of RETENTION_KEYS) {
                if (draft[key] !== settings.data?.[key]) updates[key] = draft[key];
            }
            await patch.mutateAsync(updates);
            toast.success("Retention windows saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save retention");
        }
    }

    return (
        <section className="card stack">
            <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                <div className="stack-xs">
                    <h2 className="type-h2"><Timer size={16} className="text-accent" aria-hidden /> Retention windows</h2>
                    <p className="text-13 text-text-muted" style={{ maxWidth: 620 }}>
                        How long each data class is kept before the retention sweeper considers it
                        stale. Reductions don't delete history immediately — the sweeper runs nightly.
                    </p>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "var(--space-3)" }}>
                {RETENTION_KEYS.map(({ key, label, min, max }) => (
                    <label key={key} className="stack-xs">
                        <span className="text-13">{label}</span>
                        <span className="cluster-xs">
                            <input
                                type="number"
                                min={min}
                                max={max}
                                className="input"
                                value={draft[key] ?? 0}
                                onChange={(e) => {
                                    const n = Number(e.target.value);
                                    setDraft((d) => ({ ...d, [key]: Number.isFinite(n) ? n : 0 }));
                                }}
                                style={{ maxWidth: 140 }}
                            />
                            <span className="text-12 text-text-muted">days</span>
                        </span>
                    </label>
                ))}
            </div>
            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                <button
                    type="button"
                    className="btn btn--primary btn--sm press-crush"
                    onClick={() => void save()}
                    disabled={!dirty || patch.isPending}
                >
                    <Save size={14} /> Save retention
                </button>
            </div>
        </section>
    );
}

function BrandingSection(): React.ReactElement {
    const settings = useSettings();
    const patch = usePatchSettings();
    const [theme, setTheme] = useState<string>("dataflowx");
    const [allowOverride, setAllowOverride] = useState<boolean>(true);

    useEffect(() => {
        if (!settings.data) return;
        const t = settings.data["branding.default_theme"];
        if (typeof t === "string") setTheme(t);
        const a = settings.data["branding.allow_user_override"];
        if (typeof a === "boolean") setAllowOverride(a);
    }, [settings.data]);

    const dirty = useMemo(() => {
        if (!settings.data) return false;
        return (
            theme !== settings.data["branding.default_theme"] ||
            allowOverride !== settings.data["branding.allow_user_override"]
        );
    }, [theme, allowOverride, settings.data]);

    async function save(): Promise<void> {
        try {
            await patch.mutateAsync({
                "branding.default_theme": theme,
                "branding.allow_user_override": allowOverride,
            });
            toast.success("Branding saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save branding");
        }
    }

    return (
        <section className="card stack">
            <div className="stack-xs">
                <h2 className="type-h2"><Palette size={16} className="text-accent" aria-hidden /> Branding</h2>
                <p className="text-13 text-text-muted" style={{ maxWidth: 620 }}>
                    Choose the default theme presented to every signed-in user. If user override is
                    allowed, individuals can still pick a different theme from the top bar.
                </p>
            </div>
            <div className="cluster" style={{ gap: "var(--space-3)" }}>
                <label className="stack-xs">
                    <span className="text-13">Default theme</span>
                    <select
                        className="input"
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                        style={{ minWidth: 220 }}
                    >
                        <option value="dataflowx">DataFlowX</option>
                        <option value="ember">Ember</option>
                    </select>
                </label>
                <label className="cluster-xs" style={{ alignItems: "center" }}>
                    <input
                        type="checkbox"
                        checked={allowOverride}
                        onChange={(e) => setAllowOverride(e.target.checked)}
                    />
                    <span className="text-13">Allow user override</span>
                </label>
            </div>
            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                <button
                    type="button"
                    className="btn btn--primary btn--sm press-crush"
                    onClick={() => void save()}
                    disabled={!dirty || patch.isPending}
                >
                    <Save size={14} /> Save branding
                </button>
            </div>
        </section>
    );
}

function SourceWeightsSection(): React.ReactElement {
    const weights = useFeedSourceWeights();
    const patch = usePatchFeedSourceWeights();
    const [draft, setDraft] = useState<Record<string, number>>({});

    useEffect(() => {
        if (!weights.data) return;
        const next: Record<string, number> = {};
        for (const w of weights.data) next[w.slug] = w.weight;
        setDraft(next);
    }, [weights.data]);

    const dirty = useMemo(() => {
        if (!weights.data) return false;
        for (const w of weights.data) {
            if (draft[w.slug] !== w.weight) return true;
        }
        return false;
    }, [draft, weights.data]);

    async function save(): Promise<void> {
        try {
            const updates: Record<string, number> = {};
            for (const w of weights.data ?? []) {
                if (draft[w.slug] !== w.weight) updates[w.slug] = draft[w.slug];
            }
            await patch.mutateAsync(updates);
            toast.success("Source weights saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save weights");
        }
    }

    return (
        <section className="card stack">
            <div className="stack-xs">
                <h2 className="type-h2"><SlidersHorizontal size={16} className="text-accent" aria-hidden /> Source trust weights</h2>
                <p className="text-13 text-text-muted" style={{ maxWidth: 620 }}>
                    Per-feed weighting affects platform-wide scoring. Range 0..100; 0 effectively
                    silences a source while keeping its history.
                </p>
            </div>
            {weights.isLoading ? (
                <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                    <Loader2 size={18} className="empty-state__icon animate-spin" aria-hidden />
                    <div className="empty-state__body">Loading weights…</div>
                </div>
            ) : weights.isError || !weights.data ? (
                <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                    <ShieldX size={18} className="empty-state__icon" aria-hidden />
                    <div className="empty-state__body">Could not load weights.</div>
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="data-table data-table--dense">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th style={{ whiteSpace: "nowrap", width: 1 }}>Slug</th>
                                <th style={{ whiteSpace: "nowrap", width: 1 }}>Weight</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weights.data.map((w) => (
                                <tr key={w.slug}>
                                    <td className="text-13">{w.name}</td>
                                    <td><code className="type-mono-sm text-text-muted">{w.slug}</code></td>
                                    <td>
                                        <span className="cluster-xs">
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                value={draft[w.slug] ?? 0}
                                                onChange={(e) =>
                                                    setDraft((d) => ({ ...d, [w.slug]: Number(e.target.value) }))
                                                }
                                                style={{ width: 140 }}
                                            />
                                            <span className="tabular-nums text-13" style={{ minWidth: 32, textAlign: "right" }}>
                                                {draft[w.slug] ?? 0}
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                <button
                    type="button"
                    className="btn btn--primary btn--sm press-crush"
                    onClick={() => void save()}
                    disabled={!dirty || patch.isPending}
                >
                    <Save size={14} /> Save weights
                </button>
            </div>
        </section>
    );
}

// ---- Phase 1G leftover #1 sub-items --------------------------------------

interface FidelityWeights {
    high: number;
    med: number;
    low: number;
}

/** ScoreConfigSection — verdict-builder confidence tunables.
 *  Backed by GET/PATCH /api/v2/score-config which round-trips a
 *  jsonb-per-key map (fidelity_weights / recency_half_life_days /
 *  allowlist_confidence_floor). */
function ScoreConfigSection(): React.ReactElement {
    const cfg = useScoreConfig();
    const patch = usePatchScoreConfig();

    const [weights, setWeights] = useState<FidelityWeights>({ high: 1.0, med: 0.6, low: 0.3 });
    const [halfLife, setHalfLife] = useState<number>(14);
    const [allowFloor, setAllowFloor] = useState<number>(1.0);

    useEffect(() => {
        if (!cfg.data) return;
        const fw = cfg.data["fidelity_weights"];
        if (fw && typeof fw === "object") {
            const o = fw as Record<string, unknown>;
            setWeights({
                high: typeof o.high === "number" ? o.high : 1.0,
                med:  typeof o.med  === "number" ? o.med  : 0.6,
                low:  typeof o.low  === "number" ? o.low  : 0.3,
            });
        }
        const hl = cfg.data["recency_half_life_days"];
        if (typeof hl === "number") setHalfLife(hl);
        const af = cfg.data["allowlist_confidence_floor"];
        if (typeof af === "number") setAllowFloor(af);
    }, [cfg.data]);

    const dirty = useMemo(() => {
        if (!cfg.data) return false;
        const fw = cfg.data["fidelity_weights"] as Record<string, number> | undefined;
        const hl = cfg.data["recency_half_life_days"] as number | undefined;
        const af = cfg.data["allowlist_confidence_floor"] as number | undefined;
        return (
            !fw ||
            fw.high !== weights.high ||
            fw.med  !== weights.med  ||
            fw.low  !== weights.low  ||
            hl !== halfLife ||
            af !== allowFloor
        );
    }, [weights, halfLife, allowFloor, cfg.data]);

    async function save(): Promise<void> {
        try {
            await patch.mutateAsync({
                fidelity_weights:           weights,
                recency_half_life_days:     halfLife,
                allowlist_confidence_floor: allowFloor,
            });
            toast.success("Confidence tuning saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save score config");
        }
    }

    function clamp01(n: number): number {
        if (!Number.isFinite(n) || n < 0) return 0;
        if (n > 1) return 1;
        return n;
    }

    return (
        <section className="card stack">
            <div className="stack-xs">
                <h2 className="type-h2"><Scale size={16} className="text-accent" aria-hidden /> Confidence scoring</h2>
                <p className="text-13 text-text-muted" style={{ maxWidth: 620 }}>
                    Tunables consumed by the verdict-builder when aggregating per-source
                    fidelity into a single confidence score. Changes apply on the next
                    verdict build; cached envelopes are unaffected.
                </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "var(--space-3)" }}>
                <label className="stack-xs">
                    <span className="text-13">Fidelity weight — high</span>
                    <input
                        type="number" step="0.05" min={0} max={1}
                        className="input"
                        value={weights.high}
                        onChange={(e) => setWeights((w) => ({ ...w, high: clamp01(Number(e.target.value)) }))}
                    />
                </label>
                <label className="stack-xs">
                    <span className="text-13">Fidelity weight — medium</span>
                    <input
                        type="number" step="0.05" min={0} max={1}
                        className="input"
                        value={weights.med}
                        onChange={(e) => setWeights((w) => ({ ...w, med: clamp01(Number(e.target.value)) }))}
                    />
                </label>
                <label className="stack-xs">
                    <span className="text-13">Fidelity weight — low</span>
                    <input
                        type="number" step="0.05" min={0} max={1}
                        className="input"
                        value={weights.low}
                        onChange={(e) => setWeights((w) => ({ ...w, low: clamp01(Number(e.target.value)) }))}
                    />
                </label>
                <label className="stack-xs">
                    <span className="text-13">Recency decay half-life</span>
                    <span className="cluster-xs">
                        <input
                            type="number" min={1} max={365}
                            className="input"
                            value={halfLife}
                            onChange={(e) => {
                                const n = Number(e.target.value);
                                setHalfLife(Number.isFinite(n) ? n : 14);
                            }}
                            style={{ maxWidth: 120 }}
                        />
                        <span className="text-12 text-text-muted">days</span>
                    </span>
                </label>
                <label className="stack-xs">
                    <span className="text-13">Allowlist confidence floor</span>
                    <input
                        type="number" step="0.05" min={0} max={1}
                        className="input"
                        value={allowFloor}
                        onChange={(e) => setAllowFloor(clamp01(Number(e.target.value)))}
                        style={{ maxWidth: 120 }}
                    />
                </label>
            </div>
            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                <button
                    type="button"
                    className="btn btn--primary btn--sm press-crush"
                    onClick={() => void save()}
                    disabled={!dirty || patch.isPending}
                >
                    <Save size={14} /> Save scoring
                </button>
            </div>
        </section>
    );
}

/** ApprovalGatesSection — per-category approver counts.
 *  Five gate categories shipped in seed migration; the Save button issues
 *  one PATCH /approval-gates with every changed (category, count) pair. */
function ApprovalGatesSection(): React.ReactElement {
    const gates = useApprovalGates();
    const patch = usePatchApprovalGates();

    const [draft, setDraft] = useState<Record<string, number>>({});

    useEffect(() => {
        if (!gates.data) return;
        const next: Record<string, number> = {};
        for (const g of gates.data) next[g.category] = g.approversCount;
        setDraft(next);
    }, [gates.data]);

    const dirty = useMemo(() => {
        if (!gates.data) return false;
        for (const g of gates.data) {
            if (draft[g.category] !== g.approversCount) return true;
        }
        return false;
    }, [draft, gates.data]);

    async function save(): Promise<void> {
        try {
            const updates: Record<string, number> = {};
            for (const g of gates.data ?? []) {
                if (draft[g.category] !== g.approversCount) updates[g.category] = draft[g.category];
            }
            await patch.mutateAsync(updates);
            toast.success("Approval gates saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save gates");
        }
    }

    const LABELS: Record<string, string> = {
        rule_publish:        "Rule publish",
        bulk_ioc_op:         "Bulk IoC operations",
        feed_config_change:  "Feed config change",
        tier_config_change:  "Tier config change",
        license_create:      "License create",
    };

    return (
        <section className="card stack">
            <div className="stack-xs">
                <h2 className="type-h2"><GitPullRequestArrow size={16} className="text-accent" aria-hidden /> Approval gates</h2>
                <p className="text-13 text-text-muted" style={{ maxWidth: 620 }}>
                    Number of distinct approvers required before each privileged action
                    completes. Range 1..5. Reductions are not retroactive — pending
                    reviews keep the threshold they were created under.
                </p>
            </div>
            {gates.isLoading ? (
                <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                    <Loader2 size={18} className="empty-state__icon animate-spin" aria-hidden />
                    <div className="empty-state__body">Loading gates…</div>
                </div>
            ) : gates.isError || !gates.data ? (
                <div className="empty-state" style={{ padding: "var(--space-4)" }}>
                    <ShieldX size={18} className="empty-state__icon" aria-hidden />
                    <div className="empty-state__body">Could not load gates.</div>
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="data-table data-table--dense">
                        <thead>
                            <tr>
                                <th>Action category</th>
                                <th style={{ whiteSpace: "nowrap", width: 1 }}>Category key</th>
                                <th style={{ whiteSpace: "nowrap", width: 1 }}>Approvers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gates.data.map((g) => (
                                <tr key={g.category}>
                                    <td className="text-13">{LABELS[g.category] ?? g.category}</td>
                                    <td><code className="type-mono-sm text-text-muted">{g.category}</code></td>
                                    <td>
                                        <input
                                            type="number"
                                            min={1}
                                            max={5}
                                            className="input"
                                            value={draft[g.category] ?? 1}
                                            onChange={(e) => {
                                                const n = Number(e.target.value);
                                                setDraft((d) => ({ ...d, [g.category]: Number.isFinite(n) ? n : 1 }));
                                            }}
                                            style={{ width: 72 }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                <button
                    type="button"
                    className="btn btn--primary btn--sm press-crush"
                    onClick={() => void save()}
                    disabled={!dirty || patch.isPending}
                >
                    <Save size={14} /> Save gates
                </button>
            </div>
        </section>
    );
}
